import React, { memo, useEffect, useLayoutEffect } from 'react';

import { Redirect, Route, useHistory, useLocation } from 'react-router-dom';

// Direct page-folder imports — the `pages` barrel would statically pull
// every page into the main chunk and defeat the router's code splitting.
import { ConfirmEmail } from 'pages/ConfirmEmail';
import { CreatePassword } from 'pages/CreatePassword';
import { ErrorPage } from 'pages/ErrorPage';
import { RecoveryPassword } from 'pages/RecoveryPassword';
import { Splash } from 'pages/Splash';
import { GlobalModal, GlobalToast } from 'molecules';
import { CommandPalette } from 'organisms';
import { PageShortcutsProvider } from 'context';
import { appState } from 'core';

import { useAuthState, useBootstrapHook } from 'core/hooks';

import { FundingIntakeForm } from 'pages/FundingIntakeForm';
import { AuthRouter } from './AuthRouter';
import { getAuthPath, PATHS } from './config';
import { AppRouter } from './AppRouter';

export const RootRouter = memo(() => {
  const {
    tokenRefreshed,
    data: { appState: appStateData, user },
    operations: { setBootstrapped },
  } = useBootstrapHook();
  const { data: authState } = useAuthState();
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    if (
      tokenRefreshed &&
      !appStateData.bootstrapped &&
      authState.isLoggedIn !== null &&
      user !== null
    ) {
      setBootstrapped();
    }
  }, [
    appStateData,
    authState.isLoggedIn,
    setBootstrapped,
    tokenRefreshed,
    user,
  ]);
  const history = useHistory();
  const currentState = appState();

  useEffect(() => {
    if (appStateData.networkError) {
      history.push(`/${appStateData.networkError}`);
      appState({
        ...currentState,
        networkError: null,
      });
    }
  }, [history, appStateData.networkError, currentState]);

  return appStateData.bootstrapped ? (
    <PageShortcutsProvider>
      <Route
        exact
        path="/500"
        render={() => (
          <ErrorPage status={500} isLoggedIn={authState.isLoggedIn} />
        )}
      />
      <Route
        exact
        path="/503"
        render={() => (
          <ErrorPage status={503} isLoggedIn={authState.isLoggedIn} />
        )}
      />
      <Route
        render={() => {
          if (appStateData.bootstrapped) {
            // ТЗ 0024: публичная форма проверяется ПЕРВОЙ и вне зависимости
            // от авторизации — это единственная страница приложения, до
            // которой должен доходить человек с улицы.
            if (pathname.startsWith(PATHS.dofinansowanieForm)) {
              return <FundingIntakeForm />;
            }

            if (pathname.includes(getAuthPath(PATHS.create_password))) {
              return <CreatePassword />;
            }

            if (pathname.includes(getAuthPath(PATHS.confirm_email))) {
              return (
                <ConfirmEmail
                  to={getAuthPath(PATHS.create_password) as PATHS}
                />
              );
            }

            if (authState.isLoggedIn) {
              return <AppRouter />;
            }

            if (pathname.includes(getAuthPath(PATHS.confirm_email))) {
              return (
                <ConfirmEmail
                  to={getAuthPath(PATHS.create_password) as PATHS}
                />
              );
            }

            if (pathname.includes(getAuthPath(PATHS.recovery_password))) {
              return <RecoveryPassword />;
            }

            return <Redirect to={getAuthPath(PATHS.login)} />;
          }
          return <Splash />;
        }}
      />
      <AuthRouter />
      <GlobalToast />
      <GlobalModal />
      <CommandPalette />
    </PageShortcutsProvider>
  ) : (
    <Splash />
  );
});
